/**
 * Slat layout for a fence module, driven by the model's geometry rather than by a single profile
 * width typed into the offer.
 *
 * A model's `slatWidths` is an ordered pattern that repeats across the module: [60, 100, 20] lays
 * out 60, 100, 20, 60, 100, 20, … with an equal gap between every slat. Seventeen of the twenty
 * models are a single width, and for those every formula here collapses to the `n x width` it
 * replaces, down to the same rounding.
 *
 * The widths describe the stacking direction only - how the slats follow each other across the
 * span - never how far a slat reaches across the module. H150/20I is exactly why: it stacks the
 * same 150, 20, 150, 20 as H150/20 and lays out identically here, and differs only in that its
 * narrow slats are short staggered segments rather than full-width ones. That difference belongs
 * to the materials bill and the drawing, not to this file.
 *
 * The number of gaps is expressed as an offset on the slat count, because it differs per module
 * type:
 *
 *   run opens with a slat    n slats, n - 1 gaps   offset -1
 *   run opens with a gap     n slats, n + 1 gaps   offset +1
 *   plus a reinforcement     one more gap again,   and the reinforcement takes width
 *
 * Which of the two applies is opensWithGap(): a gate always opens with a gap, and a plain panel
 * does only for the models that are built that way. A reinforcement profile sits in the run like
 * another slat, adding one more gap on top.
 */

/** Total width taken by `count` slats, cycling through the pattern. */
export function slatSpan(widths, count) {
	if (!widths || widths.length < 1 || count < 1) return 0;

	const cycle = widths.reduce((total, width) => total + Number(width), 0);
	const whole = Math.floor(count / widths.length);
	const rest = count % widths.length;

	return whole * cycle + widths.slice(0, rest).reduce((total, width) => total + Number(width), 0);
}

/**
 * The most slats that fit in `span` without dropping below `gap` between them.
 *
 * @param occupied width already spoken for inside the span (a reinforcement profile)
 */
export function maxSlatCount(widths, span, gap, gapOffset, occupied) {
	if (!widths || widths.length < 1) return 0;
	if (!(span > 0) || !widths.every(width => Number(width) > 0)) return 0;

	const taken = Number(occupied) || 0;
	const step = Number(gap);
	const narrowest = Math.min.apply(null, widths.map(Number));

	// Each further slat costs its own width plus one gap, so the pitch is what bounds the count -
	// not the width. A negative gap (overlapping slats, which is the point of the 87 models) makes
	// the pitch smaller than the slat, and more slats fit than the span would suggest. A gap that
	// cancels the narrowest slat out entirely would let slats overlap without limit.
	const pitch = narrowest + step;

	if (!(pitch > 0)) return 0;

	// the pattern makes this iterative rather than closed-form; the pitch bound keeps it finite
	const ceiling = Math.floor((span - gapOffset * step - taken) / pitch) + 2;
	let count = 0;

	while (count < ceiling) {
		const next = count + 1;
		if (slatSpan(widths, next) + taken + (next + gapOffset) * step > span) break;
		count = next;
	}

	return count;
}

/**
 * The widest gap a model is meant to be built with, in mm.
 *
 * A gap wider than the slat itself stops reading as a fence, so the model's own widest slat is
 * the cap - except on the narrow verticals (V20, V30R), which are meant to be built with gaps
 * wider than their 20 and 30 mm slats. Hence the floor: no model is held below it.
 */
export const WIDEST_SENSIBLE_GAP = 60;

export function maxSlatGap(widths) {
	if (!widths || widths.length < 1) return 0;

	return Math.max(WIDEST_SENSIBLE_GAP, Math.max.apply(null, widths.map(Number)));
}

/**
 * The fewest slats worth offering in `span` - the first count whose gap stops being wider than
 * `maxSlatGap`. Counterpart to `maxSlatCount`, which bounds the same range from the dense end.
 *
 * A count that leaves no gap at all (a single slat in an unframed panel) is not buildable either:
 * the slat would have to stretch the whole span. Those are skipped rather than accepted.
 *
 * @param occupied width already spoken for inside the span (a reinforcement profile)
 */
export function minSlatCount(widths, span, gapOffset, occupied) {
	if (!widths || widths.length < 1) return 0;
	if (!(span > 0) || !widths.every(width => Number(width) > 0)) return 0;

	const taken = Number(occupied) || 0;
	const limit = maxSlatGap(widths);
	const narrowest = Math.min.apply(null, widths.map(Number));
	// same bound as maxSlatCount, taken at the loosest gap instead of the tightest
	const ceiling = Math.floor((span - gapOffset * limit - taken) / (narrowest + limit)) + 2;
	let count = 1;

	while (count < ceiling) {
		const gaps = count + gapOffset;

		// gap <= limit, rearranged so a zero-gap count cannot divide its way past the test
		if (gaps > 0 && slatSpan(widths, count) + taken + gaps * limit >= span) return count;

		count++;
	}

	return count;
}

/**
 * The actual gap that `count` slats leave in `span`. Negative for louvre models, where the slats
 * are meant to overlap - the offer prints the absolute value.
 */
export function slatGap(widths, count, span, gapOffset, occupied) {
	const gaps = Number(count) + gapOffset;

	if (!(gaps > 0)) return 0;

	return (Number(span) - slatSpan(widths, count) - (Number(occupied) || 0)) / gaps;
}

/**
 * Models whose slat run starts and ends with a gap even when there is no frame around it.
 *
 * Most models butt their first and last slat against the edge of a plain panel. These do not -
 * the run opens and closes with a gap - so a panel of one of them has one more gap than a panel
 * of anything else, not one fewer.
 *
 * Inside a gate the question does not arise: every model's inner panel starts and ends with a
 * gap, because the frame is already there and a flush run against it reads as too heavy.
 */
const PANEL_OPENS_WITH_GAP = ["V20", "V60"];

/**
 * Does this module's slat run open and close with a gap rather than a slat?
 *
 * @param framed whether the run sits inside a frame, i.e. anything that is not a plain panel
 */
export function opensWithGap(code, framed) {
	return !!framed || PANEL_OPENS_WITH_GAP.indexOf(String(code || "").toUpperCase()) >= 0;
}

/** H models run their slats horizontally, so they stack over the height; V models over the width. */
export function isHorizontal(code) {
	return /^H/i.test(String(code || ""));
}

/** "60 / 100 / 20", the pattern as the offer shows it. */
export function slatWidthLabel(widths) {
	if (!widths || widths.length < 1) return "";

	return widths.map(width => Number(width)).join(" / ");
}

/**
 * Offer images are named after the model code and the module type. Codes carry slashes, which a
 * filename cannot, so they become dashes: H60/100/20 -> h60-100-20.
 */
export function modelImageSlug(code) {
	return String(code || "").toLowerCase().replace(/\//g, "-").replace(/\s+/g, "");
}
